import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Tooltip, IconButton, Divider, alpha, useTheme, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert } from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AiIcon,
  Bolt as BoltIcon,
  Add as AddIcon,
  Extension as ExtensionIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
  AttachFile as AttachmentIcon,
  Feedback as FeedbackIcon,
  Create as ComposeIcon,
  Search as SearchIcon,
  EventNote as EventIcon,
  ContactMail as ContactIcon,
  Note as NoteIcon,
  Bookmark as BookmarkIcon,
} from '@mui/icons-material';
import { useThemeStore } from '../../stores/themeStore';
import { useComposeTabsStore } from '../../stores/composeTabsStore';
import FeedbackDialog from './FeedbackDialog';
import HelpDialog from './HelpDialog';

interface RightActionBarProps {
  width: number;
}

const RightActionBar: React.FC<RightActionBarProps> = ({ width }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { setMode } = useThemeStore();
  const { getOrCreateTab } = useComposeTabsStore();

  const [quickActionsAnchor, setQuickActionsAnchor] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const toggleTheme = () => {
    setMode(isDark ? 'light' : 'dark');
  };

  const quickActions = [
    { label: 'Compose Email', icon: <ComposeIcon sx={{ fontSize: 18 }} />, action: () => getOrCreateTab() },
    { label: 'New Event', icon: <EventIcon sx={{ fontSize: 18 }} />, action: () => navigate('/calendar') },
    { label: 'New Task', icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, action: () => navigate('/tasks') },
    { label: 'New Contact', icon: <ContactIcon sx={{ fontSize: 18 }} />, action: () => navigate('/contacts') },
    { label: 'New Note', icon: <NoteIcon sx={{ fontSize: 18 }} />, action: () => navigate('/notes') },
    { label: 'Bookmarks', icon: <BookmarkIcon sx={{ fontSize: 18 }} />, action: () => navigate('/bookmarks') },
    { label: 'Search Mail', icon: <SearchIcon sx={{ fontSize: 18 }} />, action: () => navigate('/mail/inbox') },
  ];

  const topIcons = [
    { id: 'calendar', icon: <CalendarIcon />, label: 'Calendar', onClick: () => navigate('/calendar') },
  ];

  const middleIcons = [
    { id: 'tasks', icon: <CheckCircleIcon />, label: 'Tasks', onClick: () => navigate('/tasks') },
    { id: 'ai', icon: <AiIcon />, label: 'AI Assistant', onClick: () => navigate('/ai') },
    { id: 'attachments', icon: <AttachmentIcon />, label: 'Attachment Viewer', onClick: () => navigate('/mail/inbox') },
    { id: 'settings', icon: <SettingsIcon />, label: 'Settings', onClick: () => navigate('/settings') },
    { id: 'help', icon: <HelpIcon />, label: 'Help', onClick: () => setHelpOpen(true) },
  ];

  const bottomIcons = [
    { id: 'bolt', icon: <BoltIcon />, label: 'Quick Actions', color: '#f59e0b', onClick: (e: React.MouseEvent<HTMLElement>) => setQuickActionsAnchor(e.currentTarget) },
  ];

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 0,
        top: 64, // Below header (64px)
        width,
        height: 'calc(100vh - 64px)',
        bgcolor: isDark ? '#0c1222' : '#fafafa',
        borderLeft: `1px solid ${isDark ? '#1e293b' : '#e0e0e0'}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1,
        zIndex: 1200,
        boxShadow: 'none',
      }}
    >
      {/* Top Icons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        {topIcons.map((item) => (
          <Tooltip key={item.id} title={item.label} placement="left" arrow>
            <IconButton
              size="small"
              onClick={item.onClick}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '3px',
                color: isDark ? '#94a3b8' : '#888',
                transition: 'all 0.1s ease',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(59, 130, 246, 0.1)' : '#eee',
                  color: isDark ? '#60a5fa' : '#1561e8',
                },
              }}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      <Divider sx={{ width: '50%', my: 1, borderColor: isDark ? '#1e293b' : '#e0e0e0' }} />

      {/* Middle Icons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        {middleIcons.map((item) => (
          <Tooltip key={item.id} title={item.label} placement="left" arrow>
            <IconButton
              size="small"
              onClick={item.onClick}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '3px',
                color: isDark ? '#94a3b8' : '#888',
                transition: 'all 0.1s ease',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(59, 130, 246, 0.1)' : '#eee',
                  color: isDark ? '#60a5fa' : '#1561e8',
                },
              }}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Extension/Add-ons section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.375, mb: 0.75 }}>
        <Tooltip title="Extensions" placement="left" arrow>
          <IconButton
            size="small"
            onClick={() => setSnackbar({ open: true, message: 'Extensions marketplace coming soon!' })}
            sx={{
              width: 30,
              height: 30,
              borderRadius: '3px',
              color: isDark ? '#9ca3af' : '#888',
              '&:hover': {
                bgcolor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eee',
              },
            }}
          >
            <ExtensionIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Quick Create" placement="left" arrow>
          <IconButton
            size="small"
            onClick={(e) => setQuickActionsAnchor(e.currentTarget)}
            sx={{
              width: 30,
              height: 30,
              borderRadius: '3px',
              color: isDark ? '#9ca3af' : '#888',
              border: `1px dashed ${isDark ? '#374151' : '#ccc'}`,
              '&:hover': {
                bgcolor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eee',
                borderColor: isDark ? '#60a5fa' : '#1561e8',
              },
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ width: '55%', my: 0.375, borderColor: isDark ? '#1f2937' : '#e0e0e0' }} />

      {/* Bottom Icons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.375, mb: 0.75 }}>
        {bottomIcons.map((item) => (
          <Tooltip key={item.id} title={item.label} placement="left" arrow>
            <IconButton
              size="small"
              onClick={item.onClick}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '3px',
                color: item.color || (isDark ? '#9ca3af' : '#888'),
                transition: 'all 0.1s ease',
                '&:hover': {
                  bgcolor: alpha(item.color || '#1561e8', isDark ? 0.2 : 0.1),
                },
              }}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        ))}

        {/* Night/Day Mode Toggle */}
        <Tooltip title={isDark ? 'Switch to Day Mode' : 'Switch to Night Mode'} placement="left" arrow>
          <IconButton
            size="small"
            onClick={toggleTheme}
            sx={{
              width: 30,
              height: 30,
              borderRadius: '3px',
              color: isDark ? '#fbbf24' : '#6366f1',
              bgcolor: isDark ? 'rgba(251, 191, 36, 0.1)' : 'rgba(99, 102, 241, 0.08)',
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: isDark ? 'rgba(251, 191, 36, 0.2)' : 'rgba(99, 102, 241, 0.15)',
              },
            }}
          >
            {isDark ? <LightModeIcon sx={{ fontSize: 16 }} /> : <DarkModeIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>

        {/* Feedback */}
        <Tooltip title="Send Feedback" placement="left" arrow>
          <IconButton
            size="small"
            onClick={() => setFeedbackOpen(true)}
            sx={{
              width: 30,
              height: 30,
              borderRadius: '3px',
              color: isDark ? '#9ca3af' : '#888',
              '&:hover': {
                bgcolor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eee',
              },
            }}
          >
            <FeedbackIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Quick Actions Menu */}
      <Menu
        anchorEl={quickActionsAnchor}
        open={Boolean(quickActionsAnchor)}
        onClose={() => setQuickActionsAnchor(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              bgcolor: isDark ? '#1e293b' : '#fff',
              border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
            },
          },
        }}
      >
        {quickActions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={() => { action.action(); setQuickActionsAnchor(null); }}
            sx={{
              fontSize: '0.875rem',
              py: 1,
              color: isDark ? '#e2e8f0' : '#374151',
              '&:hover': { bgcolor: isDark ? '#334155' : '#f3f4f6' },
            }}
          >
            <ListItemIcon sx={{ color: isDark ? '#94a3b8' : '#6b7280', minWidth: 36 }}>
              {action.icon}
            </ListItemIcon>
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Feedback Dialog */}
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {/* Help Dialog */}
      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      {/* Snackbar for placeholder messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity="info" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RightActionBar;
